/**
 * Password strength rules — single source of truth for the UI-side strength
 * meter. Mirrors `backend/src/utils/password.ts::validatePasswordStrength`,
 * which is the authoritative validator on every password write path. Keep
 * the two in sync: when a rule changes here, change the backend at the
 * same time (and the in-app docs that reference the policy).
 *
 * Used by `ResetPassword.tsx` and `ChangePasswordSettings.tsx`.
 */

export interface PasswordStrengthRule {
  test: (s: string) => boolean;
  label: string;
}

export const PASSWORD_STRENGTH_RULES: PasswordStrengthRule[] = [
  { test: (s) => s.length >= 8,                                  label: 'At least 8 characters' },
  { test: (s) => /[A-Z]/.test(s),                                label: 'One uppercase letter' },
  { test: (s) => /[a-z]/.test(s),                                label: 'One lowercase letter' },
  { test: (s) => /[0-9]/.test(s),                                label: 'One number' },
  { test: (s) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/]/.test(s),   label: 'One special character' },
];

export interface EvaluatedPasswordRule extends PasswordStrengthRule {
  ok: boolean;
}

export function evaluatePasswordStrength(password: string): EvaluatedPasswordRule[] {
  return PASSWORD_STRENGTH_RULES.map((r) => ({ ...r, ok: r.test(password) }));
}

export function passwordPassesAllRules(password: string): boolean {
  return PASSWORD_STRENGTH_RULES.every((r) => r.test(password));
}
